// Unified Authentication Service for IGReview
class UnifiedAuthService {
    constructor() {
        this.API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';
        this.TOKEN_KEY = 'igreview_auth_token';
        this.USER_KEY = 'igreview_user_data';
        this.currentUser = null;
        this.authToken = null;
        
        // Initialize from localStorage
        this.loadFromStorage();
    }

    // ===============================
    // TOKEN MANAGEMENT
    // ===============================

    loadFromStorage() {
        try {
            const token = localStorage.getItem(this.TOKEN_KEY);
            const userData = localStorage.getItem(this.USER_KEY);
            
            if (token && userData) {
                this.authToken = token;
                this.currentUser = JSON.parse(userData);
                console.log('✅ Auth state restored from storage');
            }
        } catch (error) {
            console.error('Error loading auth from storage:', error);
            this.clearStorage();
        }
    }

    saveToStorage(token, user) {
        try {
            localStorage.setItem(this.TOKEN_KEY, token);
            localStorage.setItem(this.USER_KEY, JSON.stringify(user));
            this.authToken = token;
            this.currentUser = user;
        } catch (error) {
            console.error('Error saving auth to storage:', error);
        }
    }

    clearStorage() {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.USER_KEY);
        
        // Also clear legacy keys from all old authentication systems
        localStorage.removeItem('instagramUser');
        localStorage.removeItem('instagramAccessToken');
        localStorage.removeItem('instagram_user');
        localStorage.removeItem('facebook_token');
        localStorage.removeItem('verified_user');
        localStorage.removeItem('user_verification');
        localStorage.removeItem('current_user');
        localStorage.removeItem('auth_token');
        
        // Clear any session storage as well
        sessionStorage.removeItem('instagramUser');
        sessionStorage.removeItem('instagram_user');
        sessionStorage.removeItem('verified_user');
        
        this.authToken = null;
        this.currentUser = null;
        
        console.log('🧹 All authentication storage cleared');
    }

    // Force clear all old authentication data
    forceCleanAuth() {
        this.clearStorage();
        
        // Also clear any app state that might be stored
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('instagram') || key.includes('user') || key.includes('auth') || key.includes('token'))) {
                keysToRemove.push(key);
            }
        }
        
        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            console.log('🗑️ Removed legacy key:', key);
        });
        
        // Reload the page to ensure clean state
        window.location.reload();
    }

    // ===============================
    // AUTHENTICATION METHODS
    // ===============================

    async signup(userData) {
        try {
            const response = await fetch(`${this.API_BASE_URL}/api/auth-unified`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'signup',
                    ...userData
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Signup failed');
            }

            if (result.success && result.token) {
                this.saveToStorage(result.token, result.user);
                console.log('✅ Signup successful');
                return { success: true, user: result.user };
            } else {
                throw new Error(result.error || 'Signup failed');
            }
        } catch (error) {
            console.error('Signup error:', error);
            return { success: false, error: error.message };
        }
    }

    async signin(credentials) {
        try {
            const response = await fetch(`${this.API_BASE_URL}/api/auth-unified`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'signin',
                    ...credentials
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Signin failed');
            }

            if (result.success && result.token) {
                this.saveToStorage(result.token, result.user);
                console.log('✅ Signin successful');
                return { success: true, user: result.user };
            } else {
                throw new Error(result.error || 'Signin failed');
            }
        } catch (error) {
            console.error('Signin error:', error);
            return { success: false, error: error.message };
        }
    }

    async verifyUser(verificationData) {
        try {
            if (!this.currentUser?.userId) {
                throw new Error('No user found for verification');
            }

            const response = await fetch(`${this.API_BASE_URL}/api/auth-unified`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'verify',
                    userId: this.currentUser.userId,
                    ...verificationData
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Verification failed');
            }

            if (result.success && result.token) {
                this.saveToStorage(result.token, result.user);
                console.log('✅ Verification successful');
                return { success: true, user: result.user };
            } else {
                throw new Error(result.error || 'Verification failed');
            }
        } catch (error) {
            console.error('Verification error:', error);
            return { success: false, error: error.message };
        }
    }

    async refreshToken() {
        try {
            if (!this.authToken) {
                throw new Error('No token to refresh');
            }

            const response = await fetch(`${this.API_BASE_URL}/api/auth-unified`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'refresh',
                    token: this.authToken
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Token refresh failed');
            }

            if (result.success && result.token) {
                this.saveToStorage(result.token, result.user);
                console.log('✅ Token refreshed');
                return { success: true, user: result.user };
            } else {
                throw new Error(result.error || 'Token refresh failed');
            }
        } catch (error) {
            console.error('Token refresh error:', error);
            this.logout(); // Clear invalid token
            return { success: false, error: error.message };
        }
    }

    async logout() {
        try {
            // Call logout endpoint (mainly for future token blacklisting)
            if (this.authToken) {
                await fetch(`${this.API_BASE_URL}/api/auth-unified`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'logout'
                    })
                });
            }
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            this.clearStorage();
            console.log('✅ Logout successful');
        }
    }

    // ===============================
    // USER STATE MANAGEMENT
    // ===============================

    isAuthenticated() {
        return !!(this.authToken && this.currentUser);
    }

    getCurrentUser() {
        return this.currentUser;
    }

    getAuthToken() {
        return this.authToken;
    }

    hasVerification(type) {
        return this.currentUser?.verificationStatus?.[type] === true;
    }

    hasAnyVerification() {
        if (!this.currentUser?.verificationStatus) return false;
        return Object.values(this.currentUser.verificationStatus).some(status => status === true);
    }

    getUserIdentifier(type) {
        return this.currentUser?.identifiers?.[type] || null;
    }

    // ===============================
    // API REQUEST HELPERS
    // ===============================

    getAuthHeaders() {
        return {
            'Authorization': `Bearer ${this.authToken}`,
            'Content-Type': 'application/json'
        };
    }

    async authenticatedRequest(url, options = {}) {
        if (!this.authToken) {
            throw new Error('Authentication required');
        }

        // Construct full URL if relative
        const fullUrl = url.startsWith('http') ? url : `${this.API_BASE_URL}${url}`;

        let response = await fetch(fullUrl, {
            ...options,
            headers: {
                ...this.getAuthHeaders(),
                ...options.headers
            }
        });

        // Handle token expiration
        if (response.status === 401) {
            console.log('🔄 Token expired, attempting refresh...');
            const refreshResult = await this.refreshToken();
            if (refreshResult.success) {
                // Retry with new token
                response = await fetch(fullUrl, {
                    ...options,
                    headers: {
                        ...this.getAuthHeaders(),
                        ...options.headers
                    }
                });
            } else {
                throw new Error('Authentication expired');
            }
        }

        // Parse JSON response
        const result = await response.json();
        
        if (!response.ok) {
            throw new Error(result.error || result.message || `HTTP ${response.status}`);
        }

        return result;
    }

    // ===============================
    // VALIDATION & PROFILE
    // ===============================

    async validateToken() {
        try {
            const result = await this.authenticatedRequest(
                `/api/auth-middleware?action=validate`
            );

            if (result.success && result.user) {
                // Update current user with fresh data
                this.currentUser = result.user;
                localStorage.setItem(this.USER_KEY, JSON.stringify(result.user));
                return { success: true, user: result.user };
            } else {
                throw new Error('Token validation failed');
            }
        } catch (error) {
            console.error('Token validation error:', error);
            this.logout();
            return { success: false, error: error.message };
        }
    }

    async getUserReviews() {
        try {
            const result = await this.authenticatedRequest(
                `/api/auth-middleware?action=reviews`
            );

            if (result.success) {
                return { success: true, reviews: result.reviews };
            } else {
                throw new Error('Failed to fetch user reviews');
            }
        } catch (error) {
            console.error('Get user reviews error:', error);
            return { success: false, error: error.message, reviews: [] };
        }
    }

    // ===============================
    // LEGACY MIGRATION HELPERS
    // ===============================

    async migrateLegacyUser() {
        try {
            // Check for legacy Instagram login
            const instagramUser = localStorage.getItem('instagramUser');
            const instagramToken = localStorage.getItem('instagramAccessToken');
            
            if (instagramUser && instagramToken) {
                const userProfile = JSON.parse(instagramUser);
                
                // Create unified user account
                const migrationResult = await this.signup({
                    instagramUsername: userProfile.username,
                    name: userProfile.full_name || userProfile.name,
                    verificationData: {
                        instagramVerified: true
                    }
                });

                if (migrationResult.success) {
                    // Clear legacy data
                    localStorage.removeItem('instagramUser');
                    localStorage.removeItem('instagramAccessToken');
                    console.log('✅ Legacy Instagram user migrated');
                    return migrationResult;
                }
            }

            // Check for legacy Facebook login
            const savedUser = localStorage.getItem('instagram_user');
            const savedToken = localStorage.getItem('facebook_token');
            
            if (savedUser && savedToken) {
                const userProfile = JSON.parse(savedUser);
                
                const migrationResult = await this.signup({
                    facebookId: userProfile.id,
                    instagramUsername: userProfile.username,
                    name: userProfile.name,
                    verificationData: {
                        instagramVerified: true
                    }
                });

                if (migrationResult.success) {
                    localStorage.removeItem('instagram_user');
                    localStorage.removeItem('facebook_token');
                    console.log('✅ Legacy Facebook user migrated');
                    return migrationResult;
                }
            }
        } catch (error) {
            console.error('Legacy migration error:', error);
        }
        
        return null;
    }
}

// Export singleton instance
const unifiedAuthService = new UnifiedAuthService();
export default unifiedAuthService;
